import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";

/**
 * SEO: Set canonical / title / meta description SYNCHRONOUSLY before React renders.
 *
 * Why: Lovable hosting serves the same index.html for every route. Without this,
 * Googlebot's raw HTML snapshot for /blog and /blog/:slug shows the homepage
 * canonical (https://magicznakeratyna.pl/), causing Google to mark these pages as
 * "Alternate page with proper canonical tag" and skip indexing them.
 *
 * Google's Web Rendering Service does execute JS, but executing this in module
 * top-level (before React mounts) means the corrected canonical is in the DOM
 * the moment GoogleBot runs JS — no race with useEffect timing.
 *
 * Per-route metadata (titles, descriptions for individual blog posts) is still
 * updated by React components after data loads.
 */
const SITE_ORIGIN = "https://magicznakeratyna.pl";

const setMeta = (selector: string, attr: string, value: string) => {
  const el = document.querySelector(selector);
  if (el) el.setAttribute(attr, value);
};

const setCanonical = (path: string) => {
  let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", `${SITE_ORIGIN}${path}`);
};

try {
  const path = window.location.pathname;
  // Normalize trailing slash (except root)
  const normalized = path !== "/" && path.endsWith("/") ? path.slice(0, -1) : path;

  setCanonical(normalized);

  // Set route-specific OG URL so social shares & raw-HTML scrapers see the right URL
  setMeta('meta[property="og:url"]', "content", `${SITE_ORIGIN}${normalized}`);

  // Lightweight title hints for non-home routes (React will refine these on mount).
  if (normalized === "/blog") {
    document.title = "Blog o pielęgnacji włosów | Magiczna Keratyna";
    setMeta(
      'meta[name="description"]',
      "content",
      "Porady ekspertów o keratynowym prostowaniu i nanoplastii włosów. Jak dbać o włosy po zabiegu."
    );
    setMeta('meta[property="og:title"]', "content", "Blog o pielęgnacji włosów | Magiczna Keratyna");
  } else if (normalized.startsWith("/blog/")) {
    // Per-post title is set by BlogPost component once content loads.
    // For the raw HTML snapshot, at minimum strip the homepage title.
    document.title = "Blog | Magiczna Keratyna";
  }
} catch {
  // No-op: SSR-safe / defensive.
}

createRoot(document.getElementById("root")!).render(<App />);
